/**
 * Prop-account rules: balances, attempts, drawdown, consistency, position size.
 * Phase 1, module 4 of 4 — the largest, and where the four-way adjustment
 * disagreement lived.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3).
 *
 * TWO GLOBALS BECAME PARAMETERS, both deliberately:
 *   - the trade list, so nothing reads `state`
 *   - `today`, because `_attemptResetDates` and `computePositionSizeUsage` both
 *     called `effectiveCurrentDate()`. A function whose output depends on the
 *     wall clock cannot be parity-checked: the same input produces a different
 *     answer tomorrow. Injecting it makes the module deterministic and the
 *     date-dependence visible at every call site.
 */
import { effectiveTradeDate, type DatedTrade } from './session';
import { PROP_FEE_TYPES, type AdjustmentScope, type DayBoundaryMode } from './types';

// ---------------------------------------------------------------------------
// Adjustments
// ---------------------------------------------------------------------------

export interface Adjustment {
  id?: string;
  date: string | null;
  /** Free text. Membership of PROP_FEE_TYPES decides the scope. */
  type: string;
  amount: number | null;
}

/**
 * Which side of the fee line an adjustment falls on.
 *
 * `pnl`     — out-of-pocket fees (eval / reset / activation). Money that left
 *             the trader's BANK, not the account.
 * `balance` — everything else, payouts included: withdrawing from a funded
 *             account genuinely does reduce it.
 *
 * That line is the whole distinction. Conflating it made "Total P&L" mean two
 * different things on two surfaces, dragged the drawdown buffer toward the
 * floor by the fee amount — showing a trader closer to blowing the account than
 * they were — and fed an annualisation that turned a $165 fee into a ~$2,000
 * Calmar distortion.
 */
export function adjustmentScope(adj: Adjustment | null | undefined): AdjustmentScope {
  if (!adj) return 'balance';
  return (PROP_FEE_TYPES as readonly string[]).includes(adj.type) ? 'pnl' : 'balance';
}

/** True for adjustments that move the ACCOUNT. Null rows are excluded — the
 *  JSONB round-trip can hand one back. */
export function isBalanceMoving(adj: Adjustment | null | undefined): boolean {
  return !!adj && adjustmentScope(adj) === 'balance';
}

/**
 * An account's adjustments, always an array.
 *
 * ⚠ ONE helper rather than `?? []` at each site, because "each site" is exactly
 * what went wrong: the old client guards with `if (!a) return []` and
 * `(a.adjustments || [])` at four separate readers, and the port dropped the
 * guard at three of them while `isBalanceMoving` kept its null check and even
 * documented why.
 *
 * The consequence was not local. `attemptResetDates` feeds `attemptStart` →
 * `tradesInAttempt` → `accountComputedBalance` → `accountDisplayBalance` →
 * `computePeakBalance` → `computeDrawdownFloor`, so ONE account arriving without
 * the joined-in key threw a TypeError that blanked the compliance rail for
 * EVERY account, not just its own.
 */
export function adjustmentsOf(a: AccountLike | null | undefined): readonly Adjustment[] {
  return a?.adjustments ?? [];
}

/** Sum of balance-moving adjustments. Fees are excluded by construction. */
export function accountAdjustmentTotal(
  adjustments: readonly Adjustment[] | null | undefined,
): number {
  return (adjustments ?? []).reduce(
    (s, adj) => (isBalanceMoving(adj) ? s + (Number(adj.amount) || 0) : s), 0);
}

// ---------------------------------------------------------------------------
// Attempts
// ---------------------------------------------------------------------------
// A prop account can be RESET: the firm wipes it back to the starting balance
// with a fresh drawdown, and the failed attempt's trades stop counting toward
// compliance. The account keeps its id, its External ID and its whole trade
// history — only the compliance WINDOW moves. Modelling a reset as a new
// account would break trade routing and make pass rate lie, since pass rate
// counts accounts and five resets on one eval must stay one eval.

export interface AccountLike {
  id: string;
  accountType: string;
  startingBalance: number;
  currentBalance: number;
  autoBalance: boolean;
  initialPeakBalance: number | null;
  createdAt?: string | null;
  rules: AccountRulesLike;
  /**
   * ⚠ OPTIONAL, because at runtime it genuinely can be absent.
   *
   * `adjustments` is NOT a column — `ACCOUNT_COLUMNS` maps it to `null`
   * ("separate table, account_adjustments"). It is joined in at load, so an
   * account with no adjustment rows arrives with the key missing entirely.
   * Typing it as required made the type lie, and every reader dereferenced it
   * unguarded on the strength of that lie.
   */
  adjustments?: readonly Adjustment[] | null;
}

export interface AccountRulesLike {
  maxDDType?: string | null;
  maxDDValue?: number | null;
  lockMechanic?: string | null;
  lockPad?: number | null;
  dailyLossLimit?: number | null;
  profitTarget?: number | null;
  consistencyRulePct?: number | null;
  consistencyScope?: string | null;
  minTradingDays?: number | null;
  maxContracts?: number | null;
}

/** Reset dates at or before `today`, ascending. A reset dated in the FUTURE
 *  must not retroactively wipe the current attempt. */
export function attemptResetDates(a: AccountLike, today: string): string[] {
  return adjustmentsOf(a)
    .filter((adj) => adj && adj.type === 'Reset fee' && adj.date && adj.date <= today)
    .map((adj) => adj.date as string)
    .sort();
}

/** The date the CURRENT attempt began: the latest reset, else account creation. */
export function attemptStart(a: AccountLike, today: string): string | null {
  const resets = attemptResetDates(a, today);
  if (resets.length) return resets[resets.length - 1] as string;
  return a.createdAt ? String(a.createdAt).slice(0, 10) : null;
}

export function attemptNumber(a: AccountLike, today: string): number {
  return 1 + attemptResetDates(a, today).length;
}

/** A trade as this module needs it. */
export interface AccountTrade extends DatedTrade {
  accountId: string | null;
  netPnl: number | null;
  qty: number | null;
}

/**
 * This account's trades within the CURRENT attempt.
 *
 * ⚠ Scoping uses `effectiveTradeDate`, so it follows the day-boundary mode —
 * but `accountBalanceTimeline` below buckets by the RAW calendar date. That
 * asymmetry is in the original and is preserved deliberately; changing either
 * one in isolation would silently move balances.
 */
export function tradesInAttempt(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): AccountTrade[] {
  const mine = trades.filter((t) => t && t.accountId === a.id);
  const start = attemptStart(a, today);
  if (!start) return mine;
  return mine.filter((t) => {
    const d = effectiveTradeDate(t, mode);
    return !!d && d >= start;
  });
}

// ---------------------------------------------------------------------------
// Balance
// ---------------------------------------------------------------------------

export interface TimelinePoint {
  date: string;
  balance: number;
  dailyPnl: number;
  adjustments: number;
  tradeCount: number;
}

/**
 * Running balance by event date, current attempt only.
 *
 * Buckets trades by the RAW calendar date (`t.date`), not `effectiveTradeDate`
 * — see the note on `tradesInAttempt`. Adjustments before the attempt start are
 * dropped: they belong to a wiped attempt.
 */
export function accountBalanceTimeline(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): TimelinePoint[] {
  const start = attemptStart(a, today);

  const tradesByDate = new Map<string, { pnl: number; count: number }>();
  for (const t of tradesInAttempt(a, trades, today, mode)) {
    if (t.accountId !== a.id || !t.date) continue;
    const slot = tradesByDate.get(t.date) ?? { pnl: 0, count: 0 };
    slot.pnl += Number(t.netPnl) || 0;
    slot.count++;
    tradesByDate.set(t.date, slot);
  }

  const adjByDate = new Map<string, number>();
  for (const adj of adjustmentsOf(a)) {
    if (!adj?.date) continue;
    if (!isBalanceMoving(adj)) continue;
    if (start && adj.date < start) continue;
    adjByDate.set(adj.date, (adjByDate.get(adj.date) ?? 0) + (Number(adj.amount) || 0));
  }

  const dates = [...new Set([...tradesByDate.keys(), ...adjByDate.keys()])].sort();
  let running = a.startingBalance || 0;
  return dates.map((d) => {
    const trade = tradesByDate.get(d) ?? { pnl: 0, count: 0 };
    const adj = adjByDate.get(d) ?? 0;
    running += trade.pnl + adj;
    return { date: d, balance: running, dailyPnl: trade.pnl, adjustments: adj, tradeCount: trade.count };
  });
}

/** Starting balance + attempt P&L + attempt balance-moving adjustments. */
export function accountComputedBalance(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): number {
  const start = attemptStart(a, today);
  const net = tradesInAttempt(a, trades, today, mode)
    .reduce((s, t) => s + (Number(t.netPnl) || 0), 0);
  const adj = adjustmentsOf(a).reduce((s, x) => {
    if (!isBalanceMoving(x)) return s;
    if (start && x.date && x.date < start) return s;
    return s + (Number(x.amount) || 0);
  }, 0);
  return (a.startingBalance || 0) + net + adj;
}

/**
 * The balance to SHOW. **Every consumer must use this rather than the raw
 * `currentBalance` field**, which is only re-synced by the account editor —
 * the Quick-Log reset path never touches it, so after a reset the raw field is
 * the blown balance. Reading it directly is what seeded the RoR simulation from
 * a dead attempt.
 */
export function accountDisplayBalance(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): number {
  return a.autoBalance
    ? accountComputedBalance(a, trades, today, mode)
    : a.currentBalance || 0;
}

/** Highest balance reached. Carry-in applies only on the FIRST attempt — after
 *  a reset the peak restarts, or the old high-water mark would follow the
 *  trader into a fresh account. */
export function computePeakBalance(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): number {
  const start = a.startingBalance || 0;
  const onFirstAttempt = attemptNumber(a, today) === 1;
  const carryIn = !onFirstAttempt || a.initialPeakBalance == null ? null : Number(a.initialPeakBalance);
  let peak = Math.max(
    start,
    carryIn ?? -Infinity,
    accountDisplayBalance(a, trades, today, mode),
  );
  for (const pt of accountBalanceTimeline(a, trades, today, mode)) {
    if (pt.balance > peak) peak = pt.balance;
  }
  return peak;
}

// ---------------------------------------------------------------------------
// Drawdown
// ---------------------------------------------------------------------------

export type LockMechanic =
  | 'static'
  | 'trail-forever'
  | 'trail-then-freeze-at-start'
  | 'trail-then-freeze-at-start-plus-pad';

/** Older accounts carry only `maxDDType`; infer the mechanic from it. */
export function inferLockMechanic(maxDDType: string | null | undefined): LockMechanic {
  if (maxDDType === 'trailing-eod') return 'trail-then-freeze-at-start';
  if (maxDDType === 'trailing-intraday') return 'trail-forever';
  if (maxDDType === 'static') return 'static';
  return 'trail-forever';
}

/**
 * The balance at which the account is blown.
 *
 * A wrong floor here is the most expensive number in the product: it tells a
 * trader how much room they have left, and being wrong in the optimistic
 * direction ends an account.
 */
export function computeDrawdownFloor(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): number {
  const r = a.rules;
  if (!r) return 0;
  const dd = Number(r.maxDDValue) || 0;
  if (!dd) return 0;

  const start = a.startingBalance || 0;
  const peak = computePeakBalance(a, trades, today, mode);
  const lockMech = (r.lockMechanic as LockMechanic) || inferLockMechanic(r.maxDDType);
  const lockPad = Number(r.lockPad) || 0;

  if (lockMech === 'static') return start - dd;
  if (lockMech === 'trail-then-freeze-at-start') return Math.min(start, peak - dd);
  if (lockMech === 'trail-then-freeze-at-start-plus-pad') return Math.min(start + lockPad, peak - dd);
  return peak - dd;
}

export function computeDrawdownBuffer(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): number {
  return accountDisplayBalance(a, trades, today, mode) - computeDrawdownFloor(a, trades, today, mode);
}

export type DrawdownState = 'healthy' | 'caution' | 'danger' | 'failed';

export interface DrawdownBufferState {
  /** Clamped to [0,100] for a gauge. */
  pct: number;
  /** Unclamped. Above 100 the floor has trailed past start + the drawdown. */
  rawPct: number;
  state: DrawdownState;
}

/**
 * The buffer as a share of the drawdown, plus the band a gauge colours by.
 *
 * ⚠ `state` is driven by the RAW share, not the clamped one, and the two differ
 * exactly where it matters: a NEGATIVE buffer clamps to 0% and would read as
 * "danger" — the band you are in just before breaching — when the account has
 * already breached. `failed` is a different word and a different colour, and it
 * is the one the trader needs.
 *
 * ⚠ No drawdown rule is NOT a healthy account. It is an account with nothing to
 * measure, and the gauge that consumes this renders an unset tile instead. The
 * 100%/healthy return here exists only so callers asking "how compliant is it"
 * get a non-alarming answer; `accountCompliance` refuses first.
 */
export function computeDrawdownBufferState(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): DrawdownBufferState {
  const dd = Number(a?.rules?.maxDDValue) || 0;
  if (!dd) return { pct: 100, rawPct: 100, state: 'healthy' };
  const buffer = computeDrawdownBuffer(a, trades, today, mode);
  const rawPct = (buffer / dd) * 100;
  const pct = Math.max(0, Math.min(100, rawPct));
  const state: DrawdownState = buffer < 0 ? 'failed'
    : rawPct < 25 ? 'danger'
      : rawPct < 50 ? 'caution'
        : 'healthy';
  return { pct, rawPct, state };
}

// ---------------------------------------------------------------------------
// Profit target
// ---------------------------------------------------------------------------

export interface ProfitProgress {
  pct: number;
  dollarsToGo: number;
  state: 'not-started' | 'in-progress' | 'achieved';
  profitSoFar?: number;
  target?: number;
}

export function computeProfitTargetProgress(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): ProfitProgress {
  const target = Number(a.rules?.profitTarget) || 0;
  if (!target) return { pct: 0, dollarsToGo: 0, state: 'not-started' };
  const start = a.startingBalance || 0;
  const profitSoFar = accountDisplayBalance(a, trades, today, mode) - start;
  return {
    pct: Math.max(0, Math.min(100, (profitSoFar / target) * 100)),
    dollarsToGo: Math.max(0, target - profitSoFar),
    state: profitSoFar >= target ? 'achieved' : profitSoFar > 0 ? 'in-progress' : 'not-started',
    profitSoFar,
    target,
  };
}

// ---------------------------------------------------------------------------
// Consistency
// ---------------------------------------------------------------------------

export interface ConsistencyMath {
  applicable: true;
  /**
   * Is there a total for the best day to be a SHARE OF?
   *
   * ── 🚨 FALSE IS NOT "COMPLIANT", AND CONFLATING THEM PUTS NONSENSE ON SCREEN ──
   *
   * `totalProfit` is NET P&L, losing days included, so it is negative for any
   * trader currently down. A percentage of a non-positive total is meaningless —
   * this module's own comment says so — and it is reported as 0 with
   * `inCompliance: true`, which is the right ANSWER to "is this a violation?"
   * and the wrong thing to print.
   *
   * Josh's own card read **"Best day is $195 — 0% of net profit $-315"**: a
   * malformed figure, a percentage that is not one, and a green tick over both.
   * The caveat was computed here and thrown away by the renderer, which is worse
   * than never measuring it — it looks complete.
   *
   * So the fact is a FIELD rather than something each caller re-derives from
   * `totalProfit <= 0`. A screen that has to work the caveat out for itself is a
   * screen that will eventually forget to.
   */
  scoreable: boolean;
  inCompliance: boolean;
  bestDay: number;
  bestDayPct: number;
  totalProfit: number;
  thresholdPct: number;
  /** Extra profit on OTHER days that would dilute the best day under the cap. */
  growBy: number;
  /**
   * Dollars to shave off the best day instead — or NULL when no amount works.
   *
   * ⚠ NULL IS A REAL ANSWER, not a missing one. Trimming removes the money from
   * the best day AND from the net total, so the share only falls while the
   * threshold is under 100%. At exactly 100% the denominator is zero and above
   * it the answer is negative: no amount of trimming complies. The old client
   * hit this and put "$∞" on screen; a clamped figure would be worse, because a
   * fabricated $105,000 is presented to a trader as advice.
   */
  trimBy: number | null;
}

/**
 * The consistency rule: no single day may exceed `consistencyRulePct` of total
 * profit. Returns null when the rule does not apply.
 *
 * ⚠ `consistencyScope` defaults to `'always'` here. That default is
 * load-bearing: the field once failed to persist, and the two readers defaulted
 * it OPPOSITELY — one showed the consistency bar, the other hid the rail — so
 * the same account contradicted itself across a reload. The type now makes it
 * required (F), but the default stays for rows written before that.
 */
export function computeConsistencyMath(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): ConsistencyMath | null {
  const r = a.rules;
  if (!r) return null;
  const threshold = Number(r.consistencyRulePct) || 0;
  if (!threshold) return null;

  const scope = r.consistencyScope || 'always';
  const isEval = a.accountType === 'combine' || a.accountType === 'sim-funded';
  if (scope === 'none') return null;
  if (scope === 'eval-only' && !isEval) return null;
  if (scope === 'funded-only' && isEval) return null;

  const tl = accountBalanceTimeline(a, trades, today, mode);
  const profitDays = tl.filter((p) => p.dailyPnl > 0);

  // ⚠ `totalProfit` is NET P&L — losing days included — not the sum of winning
  // days. The name is a misnomer kept for callers, and net IS how firms score
  // the rule: best day as a share of net profit at payout time.
  //
  const bestDay = profitDays.length ? Math.max(...profitDays.map((p) => p.dailyPnl)) : 0;
  const totalProfit = tl.reduce((s, p) => s + p.dailyPnl, 0);

  // ⚠ INTENTIONAL DELTA vs the old client — PD-1 in PARITY-DELTAS.md, decided
  // in D28. The old function contradicted itself: with NO profitable day it
  // hardcoded totalProfit to 0, but with one green day and a negative net it
  // returned the real negative figure. Its own comment said the value is net
  // P&L including losing days, which the first branch violated.
  //
  // Net in both branches. It is also what the firm scores — best-day as a share
  // of NET profit at payout time.
  //
  // Registered BEFORE this code changed, per D16. That ordering is the
  // discipline: a delta argued for after a gate turns red is a rationalisation.
  //
  // A percentage of a non-positive total is meaningless, not a violation, so
  // inCompliance stays true either way.
  if (!profitDays.length || totalProfit <= 0) {
    return {
      applicable: true, scoreable: false, inCompliance: true, bestDay, bestDayPct: 0,
      totalProfit, thresholdPct: threshold, growBy: 0, trimBy: 0,
    };
  }

  const bestDayPct = (bestDay / totalProfit) * 100;
  const inCompliance = bestDayPct <= threshold;

  // ── The two ways out, and they are genuinely different actions ──
  //
  //   grow — earn X more on OTHER days:   best / (total + X) = pct/100
  //          → X = best * 100 / pct − total
  //   trim — shave Y off the best day:    (best − Y) / (total − Y) = pct/100
  //          → Y = (best − total * pct/100) / (1 − pct/100)
  //
  // See `trimBy` above for why the second has no answer at or above a 100%
  // threshold. Both are zero while in compliance — there is nothing to do.
  let growBy = 0;
  let trimBy: number | null = 0;
  if (!inCompliance) {
    growBy = Math.max(0, (bestDay * 100 / threshold) - totalProfit);
    const denom = 1 - threshold / 100;
    trimBy = denom > 0
      ? Math.max(0, (bestDay - totalProfit * threshold / 100) / denom)
      : null;
  }

  return {
    applicable: true,
    scoreable: true,
    inCompliance,
    bestDay,
    bestDayPct,
    totalProfit,
    thresholdPct: threshold,
    growBy,
    trimBy,
  };
}

// ---------------------------------------------------------------------------
// Trading days
// ---------------------------------------------------------------------------

export interface DaysProgress {
  tradingDays: number;
  minDays: number;
  minMet: boolean;
  /** Clamped to 100. */
  minProgress: number;
}

/**
 * Days traded against the firm's minimum. Null for accounts with no such rule —
 * a funded account is not failing a minimum it does not have.
 *
 * ⚠ SCOPED TO THE CURRENT ATTEMPT. A reset restarts the clock, and counting the
 * wiped attempt's days would mark the requirement satisfied on an account that
 * has not traded at all since.
 *
 * ⚠ Counts the RAW calendar date, not the effective one — the same asymmetry
 * `accountBalanceTimeline` carries and for the same reason: changing one of the
 * two in isolation silently moves numbers that are compared against each other.
 *
 * ⚠ THE OLD CLIENT'S "Cap: N days" LINE IS DEAD AND IS NOT PORTED. It reads
 * `rules.maxTradingDays`, a key that appears exactly once in the whole 48k-line
 * file — that read. Nothing writes it, no form offers it and it has no column,
 * so the cap can never render there either. Porting it would have added a field
 * to this client's model that nothing fills.
 */
export function computeDaysProgress(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): DaysProgress | null {
  if (a.accountType !== 'combine' && a.accountType !== 'sim-funded') return null;
  const minDays = Number(a.rules?.minTradingDays) || 0;
  const dates = new Set<string>();
  for (const t of tradesInAttempt(a, trades, today, mode)) {
    if (t.date) dates.add(t.date);
  }
  const tradingDays = dates.size;
  return {
    tradingDays,
    minDays,
    minMet: minDays > 0 ? tradingDays >= minDays : true,
    minProgress: minDays > 0 ? Math.min(100, (tradingDays / minDays) * 100) : 100,
  };
}

// ---------------------------------------------------------------------------
// Position size
// ---------------------------------------------------------------------------

export interface PositionSizeUsage {
  capContracts: number;
  todayMax: number;
  allTimeMax: number;
  todayPct: number;
  allTimePct: number;
  todayOver: boolean;
  everOver: boolean;
}

/**
 * Daily contracts used against the `maxContracts` cap. Null when no cap is set.
 *
 * Scoped to the CURRENT attempt: this renders inside the compliance gauge row,
 * and an all-time max carried over from a wiped attempt reads as a LIVE
 * violation on an account that has not breached anything.
 *
 * "Today" is the caller's session date, not raw UTC — a UTC slice puts evening
 * trades on the wrong side of the boundary.
 */
export function computePositionSizeUsage(
  a: AccountLike,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): PositionSizeUsage | null {
  const cap = Number(a.rules?.maxContracts) || 0;
  if (!cap) return null;

  const byDate = new Map<string, number>();
  for (const t of tradesInAttempt(a, trades, today, mode)) {
    if (t.accountId !== a.id || !t.date) continue;
    byDate.set(t.date, (byDate.get(t.date) ?? 0) + (Number(t.qty) || 0));
  }

  const todayMax = byDate.get(today) ?? 0;
  const allTimeMax = byDate.size ? Math.max(...byDate.values()) : 0;
  return {
    capContracts: cap,
    todayMax,
    allTimeMax,
    todayPct: Math.min(100, (todayMax / cap) * 100),
    allTimePct: Math.min(100, (allTimeMax / cap) * 100),
    todayOver: todayMax > cap,
    everOver: allTimeMax > cap,
  };
}
